import {
  type FlatSurface,
  GEOMETRY_TOLERANCE,
  isSimplePolygon,
  MAX_OUTLINE_POINTS,
  MIN_OUTLINE_POINTS,
  outlineBounds,
  polygonArea,
  type SizeMm,
  type SurfacePoint,
} from '@repo/api-contracts';
import { clampPercent, type PhotoDimensions, rectPoints } from './flat-surface-mapping';

/** 닫으려는 윤곽을 제품 영역으로 받을 수 없는 이유. 받을 수 있으면 null. */
export const validateOutline = (points: readonly SurfacePoint[]): string | null => {
  if (points.length < MIN_OUTLINE_POINTS) return `점을 ${MIN_OUTLINE_POINTS}개 이상 찍어 주세요.`;
  if (points.length > MAX_OUTLINE_POINTS) return `윤곽 점은 최대 ${MAX_OUTLINE_POINTS}개입니다.`;
  if (polygonArea(points) <= GEOMETRY_TOLERANCE || !isSimplePolygon(points))
    return '윤곽이 스스로 교차하거나 넓이가 없습니다. 점을 고친 뒤 다시 닫아 주세요.';
  const [tl, , br] = outlineBounds(points);
  if (br.x - tl.x <= GEOMETRY_TOLERANCE || br.y - tl.y <= GEOMETRY_TOLERANCE)
    return '윤곽이 너무 작습니다.';
  return null;
};

/** 넓힌 축의 좌표는 소수 여섯째 자리로 반올림한다 — 부동소수 찌꺼기(16.999999999999996)를 저장하지 않는다. */
const PERCENT_PRECISION = 1e6;
const roundPercent = (value: number): number =>
  Math.round(value * PERCENT_PRECISION) / PERCENT_PRECISION;

/**
 * 한 축의 틀 구간(사진 %) — 외접 구간 [from, to] 를 가운데 기준으로 length 만큼 넓힌다. 넓히지 않는 축은 외접 구간 그대로다.
 * 사진 밖으로 나가면 안쪽으로 밀고, 사진보다 길면 그 축만 사진 범위로 자른다(비율은 그 축에서 포기한다).
 */
const spanOf = (from: number, to: number, length: number): readonly [number, number] => {
  if (length <= to - from + GEOMETRY_TOLERANCE) return [from, to];
  const start = (from + to - length) / 2;
  const shifted = Math.min(Math.max(start, 0), 100 - length);
  return [clampPercent(roundPercent(shifted)), clampPercent(roundPercent(shifted + length))];
};

/**
 * 윤곽을 감싸는 틀(제어점 4개). 외접 사각형의 px 비율이 실측 비율과 다르면 짧은 축을 넓혀
 * 가로·세로 축척(px/mm)을 하나로 맞춘다 — 앞면·뒷면에서 점을 조금 다르게 찍어도 인쇄 영역이 찌그러지지 않는다.
 * 틀은 윤곽을 늘 감싸고(계약 rectContainsOutline), 비율이 이미 같으면 외접 사각형 그대로다.
 */
export const frameForOutline = (
  realSize: SizeMm,
  points: readonly SurfacePoint[],
  photo: PhotoDimensions,
): FlatSurface['points'] => {
  const [tl, , br] = outlineBounds(points);
  const boundsWidthPx = ((br.x - tl.x) * photo.widthPx) / 100;
  const boundsHeightPx = ((br.y - tl.y) * photo.heightPx) / 100;
  const pxPerMm = Math.max(boundsWidthPx / realSize.widthMm, boundsHeightPx / realSize.heightMm);
  const widthPercent = ((realSize.widthMm * pxPerMm) / photo.widthPx) * 100;
  const heightPercent = ((realSize.heightMm * pxPerMm) / photo.heightPx) * 100;
  const [left, right] = spanOf(tl.x, br.x, widthPercent);
  const [top, bottom] = spanOf(tl.y, br.y, heightPercent);

  return rectPoints(left, top, right, bottom);
};

/** 윤곽 → flat 표면. 4점은 실측 비율에 맞춘 틀(frameForOutline)이고 실측은 넘겨받은 상품 실측의 복사본이다. */
export const surfaceFromOutline = (
  id: string,
  realSize: SizeMm,
  points: readonly SurfacePoint[],
  photo: PhotoDimensions,
): FlatSurface => ({
  id,
  type: 'flat',
  realSize: { widthMm: realSize.widthMm, heightMm: realSize.heightMm },
  points: frameForOutline(realSize, points, photo),
  outline: { points: points.map((point) => ({ x: point.x, y: point.y })) },
});
